import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Divider,
  Chip,
  TextField,
  InputAdornment,
  Pagination,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Search,
  CalendarToday,
  PictureAsPdf,
  Download,
  Close,
  FilterList,
  DateRange,
} from '@mui/icons-material';

function CommuniquesPresse() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();

  // Données des communiqués de presse
  const [communiques] = useState([
    // ---- NOUVEAU communiqué en tête ----
    {
      id: 100,
      titre: "Publication de la Stratégie de Communication et de Diffusion des Statistiques du Système Statistique National (SSN) 2026-2030",
      date: "30/06/2026",
      categorie: "Stratégie",
      fichier: "https://api.insbu.bi/storage/documents/01KWBSSTNZGPN8JZPPDM7XPZW3.pdf",
      resume: "L'INSBU publie la stratégie nationale de communication et de diffusion des statistiques officielles pour la période 2026-2030. Élaborée dans le cadre du projet PROSTATBU, elle vise à renforcer la visibilité, l'accessibilité et l'utilisation des données statistiques au Burundi, en alignement avec les standards de la Communauté d'Afrique de l'Est (EAC).",
      image: '/assets/strategie-ssn/strategie_communication_image.jpg',
    },
    // Anciens communiqués (commentés dans le code original)
    /*
    {
      id: 1,
      titre: "Publication des résultats de l'enquête sur les indicateurs de pauvreté 2023",
      date: "15/03/2024",
      categorie: "Économie",
      fichier: "/pdf/communique-pauvrete-2023.pdf",
      resume: "L'INSBU publie les résultats de l'enquête nationale sur les indicateurs de pauvreté pour l'année 2023. Les données montrent une amélioration significative des conditions de vie dans plusieurs régions du pays.",
      image: "/images/communique1.jpg"
    },
    // ... autres communiqués ...
    */
  ]);

  // États pour les filtres
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const [selectedYear, setSelectedYear] = useState('Toutes');
  const [selectedCommunique, setSelectedCommunique] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [page, setPage] = useState(1);
  const itemsPerPage = 6;

  // Catégories disponibles (on ajoute "Stratégie")
  const categories = ['Tous', 'Économie', 'Inflation', 'Agriculture', 'Démographie', 'Commerce', 'Comptes nationaux', 'Publication', 'Développement durable', 'Stratégie'];

  // Années disponibles
  const years = ['Toutes','2026', '2025', '2024', '2023', '2022', '2021'];

  // Filtrage des communiqués
  const filteredCommuniques = communiques.filter(communique => {
    const matchesSearch = communique.titre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         communique.resume.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'Tous' || communique.categorie === selectedCategory;
    const matchesYear = selectedYear === 'Toutes' || communique.date.includes(selectedYear);
    
    return matchesSearch && matchesCategory && matchesYear;
  });

  // Pagination
  const pageCount = Math.ceil(filteredCommuniques.length / itemsPerPage);
  const paginatedCommuniques = filteredCommuniques.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  // Gestion du clic sur un communiqué
  const handleCommuniqueClick = (communique) => {
    // Si c'est le communiqué de la stratégie (id=100), rediriger vers la page dédiée
    if (communique.id === 100) {
      navigate('/publicationStrategie');
    } else {
      setSelectedCommunique(communique);
      setOpenDialog(true);
    }
  };

  // Téléchargement du fichier
  const handleDownload = (fichier) => {
    if (fichier.startsWith('http')) {
      window.open(fichier, '_blank');
    } else {
      const link = document.createElement('a');
      link.href = fichier;
      link.download = fichier.split('/').pop();
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Réinitialiser les filtres
  const resetFilters = () => {
    setSearchTerm('');
    setSelectedCategory('Tous');
    setSelectedYear('Toutes');
    setPage(1);
  };

  return (
    <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <NavigationBar />
      
      <Container maxWidth="lg" style={{ paddingTop: '60px', paddingBottom: '40px' }}>
        {/* En-tête de la page */}
        <Box mb={4}>
          <Typography 
            variant="h5" 
            gutterBottom 
            style={{ 
              color: '#388E3C',
              fontWeight: 'bold',
              marginBottom: '10px'
            }}
          >
            Communiqués de presse
          </Typography>
          
          <Typography 
            variant="body1" 
            paragraph 
            style={{ 
              fontSize: '1.1rem', 
              color: '#666',
              maxWidth: '1500px'
            }}
          >
            Retrouvez ici tous les communiqués de presse publiés par l'Institut National de la Statistique du Burundi (INSBU). 
            Ces documents officiels présentent les principales statistiques, les résultats d'enquêtes et les informations importantes concernant l'activité statistique nationale.
          </Typography>
        </Box>

        {/* Barre de recherche et filtres */}
        <Box mb={4} p={3} sx={{ backgroundColor: 'white', borderRadius: 2, boxShadow: 1 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Rechercher un communiqué..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box display="flex" flexDirection={isMobile ? "column" : "row"} gap={2}>
                <Chip
                  icon={<FilterList />}
                  label={`Catégorie: ${selectedCategory}`}
                  variant={selectedCategory !== 'Tous' ? "filled" : "outlined"}
                  onClick={() => {
                    const index = categories.indexOf(selectedCategory);
                    const nextIndex = (index + 1) % categories.length;
                    setSelectedCategory(categories[nextIndex]);
                  }}
                  style={{ cursor: 'pointer', color: selectedCategory !== 'Tous' ? '#388E3C' : 'inherit' }}
                />
                
                <Chip
                  icon={<DateRange />}
                  label={`Année: ${selectedYear}`}
                  variant={selectedYear !== 'Toutes' ? "filled" : "outlined"}
                  onClick={() => {
                    const index = years.indexOf(selectedYear);
                    const nextIndex = (index + 1) % years.length;
                    setSelectedYear(years[nextIndex]);
                  }}
                  style={{ cursor: 'pointer' }}
                />

                <Button
                  variant="outlined"
                  size="small"
                  onClick={resetFilters}
                  style={{ marginLeft: isMobile ? 0 : 'auto' }}
                >
                  Réinitialiser
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Statistiques */}
        <Box mb={3}>
          <Typography variant="body2" color="textSecondary">
            {filteredCommuniques.length} communiqué{filteredCommuniques.length !== 1 ? 's' : ''} trouvé{filteredCommuniques.length !== 1 ? 's' : ''}
          </Typography>
        </Box>

        {/* Liste des communiqués */}
        {filteredCommuniques.length > 0 ? (
          <>
            <Grid container spacing={3}>
              {paginatedCommuniques.map((communique) => (
                <Grid item xs={12} sm={6} md={4} key={communique.id}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3,
                        cursor: 'pointer'
                      }
                    }}
                    onClick={() => handleCommuniqueClick(communique)}
                  >
                    <Box
                      sx={{
                        height: 140,
                        backgroundColor: '#e8f5e9',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative'
                      }}
                    >
                      {communique.image ? (
                        <img
                          src={communique.image}
                          alt={communique.titre}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = `<svg ...>`; // fallback
                          }}
                        />
                      ) : (
                        <PictureAsPdf sx={{ fontSize: 60, color: '#388E3C' }} />
                      )}
                      <Chip
                        label={communique.categorie}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                          backgroundColor: '#388E3C',
                          color: 'white'
                        }}
                      />
                    </Box>

                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box display="flex" alignItems="center" mb={1}>
                        <CalendarToday sx={{ fontSize: 16, color: '#666', mr: 1 }} />
                        <Typography variant="caption" color="textSecondary">
                          {communique.date}
                        </Typography>
                      </Box>
                      
                      <Typography 
                        variant="h6" 
                        gutterBottom
                        sx={{ 
                          fontSize: '1.1rem',
                          fontWeight: 'bold',
                          color: '#333',
                          lineHeight: 1.3,
                          mb: 2
                        }}
                      >
                        {communique.titre}
                      </Typography>
                      
                      <Typography 
                        variant="body2" 
                        color="textSecondary"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      >
                        {communique.resume}
                      </Typography>
                    </CardContent>

                    <CardActions sx={{ justifyContent: 'space-between', p: 2, pt: 0 }}>
                      <Button 
                        size="small" 
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDownload(communique.fichier);
                        }}
                      >
                        Télécharger
                      </Button>
                      <Button 
                        size="small" 
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCommuniqueClick(communique);
                        }}
                      >
                        En savoir plus
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {pageCount > 1 && (
              <Box mt={4} display="flex" justifyContent="center">
                <Pagination
                  count={pageCount}
                  page={page}
                  onChange={(event, value) => setPage(value)}
                  color="primary"
                  size={isMobile ? "small" : "medium"}
                />
              </Box>
            )}
          </>
        ) : (
          <Box 
            textAlign="center" 
            py={8} 
            sx={{ 
              backgroundColor: 'white', 
              borderRadius: 2,
              boxShadow: 1
            }}
          >
            <Search sx={{ fontSize: 60, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" color="textSecondary" gutterBottom>
              Aucun communiqué trouvé
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Aucun communiqué ne correspond à votre recherche. Essayez avec d'autres mots-clés.
            </Typography>
            <Button 
              variant="outlined" 
              color="primary"
              sx={{ mt: 2 }}
              onClick={resetFilters}
            >
              Voir tous les communiqués
            </Button>
          </Box>
        )}

        {/* Section d'information */}
        <Box mt={6} p={3} sx={{ backgroundColor: '#e8f5e9', borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom style={{ color: '#388E3C' }}>
            À propos des communiqués de presse
          </Typography>
          <Typography variant="body2" paragraph>
            Les communiqués de presse de l'INSBU sont des documents officiels qui présentent les principaux 
            résultats statistiques, les lancements d'enquêtes, les publications importantes et les événements 
            majeurs de l'institut.
          </Typography>
        </Box>
      </Container>

      {/* Dialog pour afficher les détails d'un communiqué (hors stratégie) */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedCommunique && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h6" style={{ color: '#388E3C' }}>
                  {selectedCommunique.titre}
                </Typography>
                <IconButton onClick={() => setOpenDialog(false)} size="small">
                  <Close />
                </IconButton>
              </Box>
            </DialogTitle>
            
            <DialogContent dividers>
              <Box mb={2} display="flex" alignItems="center" flexWrap="wrap" gap={1}>
                <CalendarToday sx={{ fontSize: 16, color: '#666', mr: 1 }} />
                <Typography variant="body2" color="textSecondary" mr={2}>
                  Publié le: {selectedCommunique.date}
                </Typography>
                <Chip 
                  label={selectedCommunique.categorie} 
                  size="small" 
                  sx={{ backgroundColor: '#388E3C', color: 'white' }}
                />
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="body1" paragraph>
                {selectedCommunique.resume}
              </Typography>
              
              <Box mt={3} p={2} sx={{ backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Informations complémentaires
                </Typography>
                <Typography variant="body2">
                  Ce communiqué de presse est disponible en format PDF. Pour toute demande d'information 
                  complémentaire ou pour organiser une interview avec nos experts, veuillez contacter 
                  le service de communication de l'INSBU.
                </Typography>
              </Box>
            </DialogContent>
            
            <DialogActions>
              <Button onClick={() => setOpenDialog(false)} color="inherit">
                Fermer
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                startIcon={<Download />}
                onClick={() => {
                  handleDownload(selectedCommunique.fichier);
                  setOpenDialog(false);
                }}
              >
                Télécharger le PDF
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Footer />
    </div>
  );
}

export default CommuniquesPresse;